/**
 * @file     NotificationCenterProvider.tsx
 * @purpose  Shared client store for authoritative notification pages, unread
 *           totals, read commands, and private real-time reconciliation.
 *           @/services/anti-scraping/secureFetch, @/services/realtime/topics
 */
'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLiveChannel } from '@/hooks/useLiveChannel';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { topics } from '@/services/realtime/topics';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import type { ActivityEventRow } from '@/services/activity/types';
import type {
  NotificationCenterApiPage,
  NotificationCenterScope,
} from '@/services/notifications/notificationCenterTypes';

type LiveTopic = 'admin' | 'user';

type NotificationCenterValue = {
  scope: NotificationCenterScope;
  items: ActivityEventRow[];
  unreadCount: number;
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  connected: boolean;
  hasMore: boolean;
  latestEvent: ActivityEventRow | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (eventId: string, read?: boolean) => Promise<void>;
  markAllRead: () => Promise<void>;
};

const NotificationCenterContext = createContext<NotificationCenterValue | null>(null);

export function NotificationCenterProvider({
  children,
  scope,
  liveTopic = scope === 'admin' ? 'admin' : 'user',
}: {
  children: ReactNode;
  scope: NotificationCenterScope;
  liveTopic?: LiveTopic;
}) {
  const { user, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<ActivityEventRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [latestEvent, setLatestEvent] = useState<ActivityEventRow | null>(null);
  const initializedRef = useRef(false);
  const itemsRef = useRef<ActivityEventRow[]>([]);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  itemsRef.current = items;

  const applyPage = useCallback((page: NotificationCenterApiPage, append: boolean) => {
    if (!append && initializedRef.current) {
      const known = new Set(itemsRef.current.map((item) => item.id));
      const arrived = page.items.find((item) => !known.has(item.id));
      if (arrived) setLatestEvent(arrived);
    }
    setItems((previous) => {
      if (!append) return page.items;
      const byId = new Map(previous.map((item) => [item.id, item]));
      for (const item of page.items) byId.set(item.id, item);
      return [...byId.values()].sort((a, b) => (
        b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id)
      ));
    });
    setUnreadCount(Math.max(0, page.unreadCount));
    setTotalCount(Math.max(0, page.totalCount));
    setNextCursor(page.nextCursor);
    initializedRef.current = true;
    setError(false);
  }, []);

  const requestPage = useCallback(async (cursor: string | null) => {
    const query = new URLSearchParams({ scope, limit: '25' });
    if (cursor) query.set('cursor', cursor);
    const response = await fetch(`/api/notification-center?${query.toString()}`, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`notification_center_${response.status}`);
    return response.json() as Promise<NotificationCenterApiPage>;
  }, [scope]);

  const refresh = useCallback(async () => {
    if (!user || authLoading) return;
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const operation = (async () => {
      setLoading(!initializedRef.current);
      try {
        applyPage(await requestPage(null), false);
      } catch (cause) {
        reportSilentFailure(cause, { area: 'notification-center/client', route: 'refresh' });
        setError(true);
      } finally {
        setLoading(false);
        refreshPromiseRef.current = null;
      }
    })();
    refreshPromiseRef.current = operation;
    return operation;
  }, [applyPage, authLoading, requestPage, user]);

  const loadMore = useCallback(async () => {
    if (!user || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      applyPage(await requestPage(nextCursor), true);
    } catch (cause) {
      reportSilentFailure(cause, { area: 'notification-center/client', route: 'loadMore' });
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [applyPage, loadingMore, nextCursor, requestPage, user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      initializedRef.current = false;
      setItems([]);
      setUnreadCount(0);
      setTotalCount(0);
      setNextCursor(null);
      setLatestEvent(null);
      setError(false);
      return;
    }
    initializedRef.current = false;
    itemsRef.current = [];
    setItems([]);
    setUnreadCount(0);
    setTotalCount(0);
    setNextCursor(null);
    setLatestEvent(null);
    setError(false);
    void refresh();
  }, [authLoading, refresh, scope, user?.id]);

  const topic = !user
    ? null
    : liveTopic === 'admin'
      ? topics.admin()
      : topics.user(user.id);
  const { connected } = useLiveChannel({
    topic,
    resource: 'notifications',
    onChange: refresh,
  });

  const markRead = useCallback(async (eventId: string, read = true) => {
    const readField = scope === 'admin' ? 'read_by_admin_at' : 'read_by_user_at';
    const current = itemsRef.current.find((item) => item.id === eventId);
    const wasUnread = current ? !current[readField] : false;
    const timestamp = read ? new Date().toISOString() : null;
    setItems((previous) => previous.map((item) => (
      item.id === eventId ? { ...item, [readField]: timestamp } : item
    )));
    if (wasUnread !== !read) {
      setUnreadCount((count) => Math.max(0, count + (read ? -1 : 1)));
    }
    try {
      const response = await secureFetch(`/api/notification-center/${eventId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scope, read }),
      });
      if (!response.ok) throw new Error(`notification_read_${response.status}`);
    } catch (cause) {
      reportSilentFailure(cause, { area: 'notification-center/client', route: 'markRead' });
      await refresh();
    }
  }, [refresh, scope]);

  const markAllRead = useCallback(async () => {
    const readField = scope === 'admin' ? 'read_by_admin_at' : 'read_by_user_at';
    const timestamp = new Date().toISOString();
    setItems((previous) => previous.map((item) => ({ ...item, [readField]: timestamp })));
    setUnreadCount(0);
    try {
      const response = await secureFetch('/api/notification-center/read-all', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scope }),
      });
      if (!response.ok) throw new Error(`notification_read_all_${response.status}`);
    } catch (cause) {
      reportSilentFailure(cause, { area: 'notification-center/client', route: 'markAllRead' });
      await refresh();
    }
  }, [refresh, scope]);

  const value = useMemo<NotificationCenterValue>(() => ({
    scope,
    items,
    unreadCount,
    totalCount,
    loading,
    loadingMore,
    error,
    connected,
    hasMore: nextCursor !== null,
    latestEvent,
    refresh,
    loadMore,
    markRead,
    markAllRead,
  }), [
    connected,
    error,
    items,
    latestEvent,
    loadMore,
    loading,
    loadingMore,
    markAllRead,
    markRead,
    nextCursor,
    refresh,
    scope,
    totalCount,
    unreadCount,
  ]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function useNotificationCenter(expectedScope?: NotificationCenterScope): NotificationCenterValue {
  const value = useContext(NotificationCenterContext);
  if (!value) throw new Error('useNotificationCenter must be used inside NotificationCenterProvider');
  if (expectedScope && value.scope !== expectedScope) {
    throw new Error(`Notification center scope mismatch: expected ${expectedScope}, received ${value.scope}`);
  }
  return value;
}

export function useOptionalNotificationCenter(): NotificationCenterValue | null {
  return useContext(NotificationCenterContext);
}
